import React, { ChangeEvent, useCallback, FC, useMemo } from 'react';
import { Field, FieldProps } from 'formik';
import _map from 'lodash/map';
import _union from 'lodash/union';
import _uniqBy from 'lodash/uniqBy';
import _intersection from 'lodash/intersection';
import _intersectionBy from 'lodash/intersectionBy';
import _difference from 'lodash/difference';
import _differenceBy from 'lodash/differenceBy';
import styled from 'styled-components';
import { setFieldValueAndTouched } from '@minecraft.utils';
import Checkbox from '@minecraft.checkbox';

/** Types */
type Props = {
  className?: string;
  disabled?: boolean;
  groupName: string;
  label?: string;
  value: any[];
  saveAsOption?: boolean;
  handleClick?: (event?: any) => void;
};

type SelectAllCheckboxProps = Props & FieldProps;

/** Styled Components */
const StyledContainer = styled.div`
  border-bottom: 1px solid ${(props): string => props.theme.colors.primary.ltGrey1};
  margin-bottom: 0.75rem;
`;

const shouldBeChecked = ({ saveAsOption, allOptions, selectedOptions }) => {
  if (!selectedOptions.length) {
    return false;
  }

  if (saveAsOption) {
    return allOptions?.length === _intersectionBy(allOptions, selectedOptions, 'value')?.length;
  }

  return allOptions?.length === _intersection(allOptions, selectedOptions)?.length;
};

const deselect = (saveAsOption, allOptions, selectedOptions) => {
  return saveAsOption ? _differenceBy(selectedOptions, allOptions, 'value') : _difference(selectedOptions, allOptions);
};

const select = (saveAsOption, allOptions, selectedOptions) => {
  return saveAsOption ? _uniqBy([...allOptions, ...selectedOptions], 'value') : _union(allOptions, selectedOptions);
};

/** Help Components */
const SelectAllCheckbox: FC<SelectAllCheckboxProps> = ({
  saveAsOption = false,
  label,
  groupName,
  value,
  field,
  form,
  children,
  className,
  handleClick,
  ...restProps
}) => {
  const allOptions = useMemo(
    () => (saveAsOption ? value : _map(value, (item) => item?.value || item)),
    [saveAsOption, value]
  );
  const isChecked = shouldBeChecked({ saveAsOption, allOptions, selectedOptions: field?.value });

  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const newValue = isChecked
        ? deselect(saveAsOption, allOptions, field.value)
        : select(saveAsOption, allOptions, field.value);

      setFieldValueAndTouched(field.name, newValue, form);

      if (handleClick) {
        handleClick(event);
      }
    },
    [form, field, allOptions, isChecked, handleClick]
  );

  return (
    <StyledContainer className={className}>
      <Checkbox isChecked={isChecked} label={label} onChange={handleCheckboxChange} {...restProps} />
      {children}
    </StyledContainer>
  );
};

/** Main Component */
/**
 * @deprecated This is an older component that does not use our standard form wrapper
 * to match the design system.
 *
 * If this component is used in application code, we want to replace it and the other related checkboxes
 * with a CheckboxGroupInput where the withSelectAll prop is set
 *
 * import { CheckboxGroupInput } from '@minecraft.molecules'
 *
 * // where fiieldName is a list of values in the formik state
 * <CheckboxGroupInput withSelectAll name="fieldName" label="Field Label" options={[{ value: '1', label: 'option 1' }, { value: '2', label: 'option 2' }]} />
 * for any props that are not currently implemented we should add the functionality
 */
const FormikSelectAllCheckbox: FC<Props> = (props) => (
  <Field component={SelectAllCheckbox} name={props.groupName} {...props} />
);

export default FormikSelectAllCheckbox;
